import { useEffect, useRef } from 'react';
import { useAuthStore, useWorkplaceStore } from '@host/stores';
import { useUserProfileStore } from '@host/stores/user-profile.store';
import { Logger } from '../../../shared/services/logger.service';
import { useMyFloorStore } from '@apps/office/stores/myFloorStore';
import { useMultiFloorStateStore } from '@apps/office/stores/MultiFloorStore';
import { getReloadRoomSnapshot } from '@host/utils/reload-room-snapshot';
import { RESTORE_KEYS } from '@host/utils/pending-room-restore.util';

function getWorkplacePresenceContext() {
  const workplaceStore = useWorkplaceStore.getState();
  const profile = workplaceStore.profile;
  return {
    homeRoomPid: profile?.home_room_pid,
    orgPid: workplaceStore.org?.pid || profile?.org_pid,
    // currentFloorPresence is set synchronously by OfficeService.navigateToRoom
    // (setCurrentFloorPresence) the moment a live room-change happens.
    // workplaceStore.currentRoom/presence don't reliably carry the floor the
    // user just navigated to right away (currentRoom's floor_pid can be
    // missing, and presence.floor_pid lags behind local navigation until the
    // next server sync), which caused beforeunload to sometimes stash the
    // WRONG (stale/home) floor immediately after navigating into a room.
    floorPid:
      useMultiFloorStateStore.getState().currentFloorPresence ||
      workplaceStore.currentRoom?.floor_pid ||
      workplaceStore.presence?.floor_pid ||
      workplaceStore.homeRoom?.floor_pid ||
      profile?.home_floor_pid,
  };
}

export function usePresenceLifecycle() {
  const userProfile = useUserProfileStore((s) => s.profile);
  // Restoration must wait for loadFloorsSelected() to finish its wholesale
  // set({ floorsSelected }) — otherwise it lands first and gets stomped once
  // that fetch resolves (see MultiFloorStore.ts loadFloorsSelected). This flag
  // is not persisted, so it reliably starts false on every fresh page load.
  const floorsSelectedLoaded = useMultiFloorStateStore((s) => s.floorsSelectedLoaded);

  // Hold auth token in a ref for use by beforeunload/load handlers; updated asynchronously when the user profile changes
  const tokenRef = useRef<string | null>(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    const updateToken = async () => {
      if (!userProfile?.pid) {
        tokenRef.current = null;
        return;
      }
      try {
        const token = await useAuthStore.getState().getToken();
        tokenRef.current = token;
      } catch (error) {
        Logger.debug(
          '[PresenceLifecycle] Failed to retrieve auth token for beforeunload handler',
          error
        );
        tokenRef.current = null;
      }
    };
    updateToken();
  }, [userProfile?.pid]);

  // Restore floor/room presence once floorsSelected has actually finished
  // loading, instead of on the raw window 'load' event — that event has no
  // ordering relationship with the app's own async bootstrap (auth, profile,
  // loadFloorsSelected), so restoring on it raced the app's own default-floor
  // selection and its subsequent full-store overwrite, silently discarding
  // the restore.
  useEffect(() => {
    if (!floorsSelectedLoaded || restoredRef.current) return;

    const performRestore = async () => {
      // ❌ Skip fresh tab open (and reloads from the home room — nothing is stashed for those)
      const snapshot = getReloadRoomSnapshot();
      if (!snapshot) return;

      restoredRef.current = true;

      const { homeRoomPid, floorPid: fallbackFloorPid } = getWorkplacePresenceContext();
      // Prefer the floor stashed right before unload — by the time this handler
      // runs, currentRoom/presence haven't been rehydrated yet, so the context
      // helper's fallback chain resolves to the user's HOME floor instead of the
      // floor they actually refreshed from.
      const floorPid = snapshot.floorPid || fallbackFloorPid;

      // ✅ SKIP if home room
      if (snapshot.roomPid === homeRoomPid) return;

      if (!floorPid) return;

      // NGE-1305: the room itself (presence, meeting, video, background and the
      // broadcast to other clients) is restored by WorkplaceService.initializeWorkplace
      // through the full navigateToRoom flow. This handler used to POST the presence and
      // patch the avatar on its own, which put the avatar back in the meeting room while
      // the bootstrap had already joined the home room's meeting.

      // The bootstrap carousel/floor view defaults `currentFloorId` to the
      // user's first selected floor on a fixed timer as soon as floor data
      // loads, independent of and racing this handler's own network-bound
      // restore — whichever finishes last wins, which is why this was flaky.
      // isRestoringFloor tells CarouselView to defer its own default-floor
      // selection for as long as this restore is in flight.
      useMyFloorStore.getState().setIsRestoringFloor(true);
      try {
        const multiFloorStore = useMultiFloorStateStore.getState();
        if (!multiFloorStore.floorsSelected[floorPid]) {
          multiFloorStore.setFloorSelected(floorPid, true);
        }
        const currentFloorPresence = multiFloorStore.currentFloorPresence;
        const newPreview =
          currentFloorPresence && currentFloorPresence !== floorPid
            ? [currentFloorPresence, floorPid]
            : [floorPid];
        await multiFloorStore.setFloorsPreview(newPreview);
        useMyFloorStore.getState().setCurrentFloorId(floorPid);
      } catch (err) {
        Logger.debug('[PresenceLifecycle] Failed to restore floor navigation on load:', err);
      } finally {
        useMyFloorStore.getState().setIsRestoringFloor(false);
      }
    };

    // Always release the default-floor hold, including on early returns.
    const restore = async () => {
      try {
        await performRestore();
      } finally {
        useMyFloorStore.getState().setIsRestoringFloor(false);
      }
    };

    restore();
  }, [floorsSelectedLoaded]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const workplaceStore = useWorkplaceStore.getState();
      const userPid = useUserProfileStore.getState().profile?.pid;

      const roomPid = workplaceStore.currentRoom?.pid || workplaceStore.presence?.room_pid;
      const { homeRoomPid, orgPid, floorPid } = getWorkplacePresenceContext();

      // ✅ SKIP if already in home room
      if (!roomPid || roomPid === homeRoomPid) {
        return;
      }

      // 👇 This is the KEY flag
      sessionStorage.setItem(RESTORE_KEYS.refreshing, '1');
      sessionStorage.setItem(RESTORE_KEYS.roomPid, roomPid);
      if (floorPid) {
        sessionStorage.setItem(RESTORE_KEYS.floorPid, floorPid);
      }

      // Preserve locked status so it can be restored after reload
      const currentStatus = workplaceStore.user?.status;
      if (currentStatus === 'locked') {
        sessionStorage.setItem(RESTORE_KEYS.status, currentStatus);
      }

      if (!floorPid || !userPid || !orgPid || !tokenRef.current) return;

      try {
        const presenceRequest = fetch(
          `${import.meta.env.VITE_APP_API_ROOT}/workplace/user/presence`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenRef.current}`,
              'x-org-pid': orgPid,
            },
            body: JSON.stringify({
              floorId: floorPid,
              roomId: roomPid,
              userId: userPid,
              status: currentStatus || 'active',
            }),
            keepalive: true,
          }
        );
        presenceRequest.catch((error) => {
          // Logging for debugging failed beforeunload presence updates
          Logger.debug(
            '[PresenceLifecycle] Failed to update user presence on beforeunload:',
            error
          );
        });
      } catch (error) {
        // Catch any synchronous errors thrown before the request is sent
        Logger.debug(
          '[PresenceLifecycle] Error initiating user presence update on beforeunload:',
          error
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
}
